import { hasModule } from "csi/_base/modules";
import def from "csi/def";
import reflection from "csi/reflection";
import sessionConstants from "csi/sessionConstants";
import {
	DataSetupGroup,
	DataSetupItem,
	DataSetupSection,
	DataSetupSectionId,
	DataSetupSubsection,
	GROUP_SERVICES,
	URL_TYPE_MAP,
	ViewURL
} from "csi/state/datasetup";
import { featureTogglesStore } from "csi/state/rootStore";

const {
	URL_APPAREL,
	URL_CALENDAR,
	URL_DATA,
	URL_LIB_SUPPLIERITEM,
	URL_QUALITY,
	URL_QUALITY_ADMIN,
	URL_QUESTION_SECTION,
	URL_QUEUES_SERVICES,
	URL_SIZE_CHART,
	URL_SOURCING,
	URL_SOURCING_VIEW,
	URL_WBS_ADMIN,
} = ViewURL;

const MODULE_APPAREL = "Apparel";
const MODULE_FORMULATED_PRODUCT = "FormulatedProduct";
const MODULE_HIGG = "HiggConnect";
const MODULE_PRODUCT_COMPLIANCE_SYSTEM = "ProductComplianceSystem";
const MODULE_PROOFING = "Proofing";
const MODULE_VIPBOARD = "VIPBoard";

/**
 * Generate the Subtype Group
 * @param groupResourceId the resource id of the group
 * @param subtypedType type for which subtypes exist
 * @param itemType type of item to display in data setup
 * @param rootUrl url of the node which this view belongs to
 * @param viewId view id
 * @param resourceId resource id if missing the types
 */
function generateSubtypeGroup(
	groupResourceId: string,
	subtypedType: string,
	itemType: string,
	rootUrl: string,
	viewId: string,
	resourceId: string,
): DataSetupGroup | null {
	const viewOwnerType = URL_TYPE_MAP[rootUrl];
	const defView = def.getClassViewDef(reflection.getClass(viewOwnerType), viewId);

	// if the view doesn't exist, just return null
	if (!defView) {
		return null;
	}

	const subtypeList = reflection.getSubtypes(subtypedType);
	const items = subtypeList?.map((node) => new DataSetupItem(itemType, rootUrl, viewId, null, node.$URL, node.$Name)) || [];
	items.sort((a, b) => a.name.localeCompare(b.name));

	return new DataSetupGroup(groupResourceId, items, !items.length ? resourceId : null);
}

/**
 * Return all sections in data setup
 */
export function getSections(): (DataSetupSection | DataSetupSubsection)[] {
	const sections = [
		getGeneralSection(),
		getHierarchySection(),
		getSpecificationSection(),
		getSourcingSection(),
		getQualitySection(),
		getSalesSection(),
		getScheduleSection(),
		getServicesSection(),
		getSustainabilitySection(),
	];

	if (hasModule(MODULE_PROOFING)) {
		sections.push(getProofingTemplateSection());
	}

	if (hasModule(MODULE_VIPBOARD)) {
		sections.push(getVipBoardSection());
	}

	if (hasModule(MODULE_PRODUCT_COMPLIANCE_SYSTEM)) {
		sections.push(getProductComplianceSection());
	}

	return sections;
}

// populate sections
function getGeneralSection(): DataSetupSection {
	const children = [
		new DataSetupGroup("", [
			new DataSetupItem("Language", URL_SIZE_CHART, "Language", [
				"CareSymbol",
				"CompositionPlacement",
				"Composition",
				"Country",
			]),
			new DataSetupItem("Country", URL_SOURCING, "Countries", [
				"Language",
				"ShippingPort",
				"ProductSalesRegionSpec",
			]),
			new DataSetupItem("Currency", URL_DATA, "Currency", [
				"CurrencyExchangeTable",
			]),
			new DataSetupItem("CurrencyExchangeTable", URL_DATA, "CurrencyTables", [
				"Currency",
			]),
			new DataSetupItem("UOMType", URL_DATA, "UOMTypes"),
			new DataSetupItem("UOMConversion", URL_DATA, "UOMConversions"),
		]),
		generateSubtypeGroup("DataSetupGroupLookupItems", "LookupItem", "LookupItem", URL_DATA, "LookupItems", "MissingLookupItems"),
	];

	return new DataSetupSection(DataSetupSectionId.GENERAL, children);
}

function getHierarchySection(): DataSetupSection {
	const children = [
		new DataSetupGroup("", [
			new DataSetupItem("StructItemNameCode", URL_APPAREL, "StructItemNameCodes"),
			new DataSetupItem("Style", URL_APPAREL, "StyleTemplates"),
			new DataSetupItem("Classifier0", URL_APPAREL, "Classifiers"),
			new DataSetupItem("Classifier3", URL_APPAREL, "Classifier3s"),
		]),
	];

	return new DataSetupSection(DataSetupSectionId.HIERARCHY, children);
}

function getSpecificationSection(): DataSetupSection {
	const children = [
		new DataSetupSubsection("DataSetupSubsectionColorPrint", [
			new DataSetupGroup("DataSetupGroupColor", [
				new DataSetupItem("ColorPalette", URL_APPAREL, "ColorPalettes", [
					"ColorSpecification",
				]),
				new DataSetupItem("ColorSpecification", URL_APPAREL, "ColorSpecifications", [
					"LibColorSpecification",
					"PrintSpecification",
					"PrintDesign",
				]),
				new DataSetupItem("LibColorSpecification", URL_SIZE_CHART, "LibrariesLibColorSpecification", [
					"ColorSpecification",
				]),
			]),
			new DataSetupGroup("DataSetupGroupPrint", [
				new DataSetupItem("PrintSpecification", URL_APPAREL, "PrintSpecifications", [
					"ColorSpecification",
				]),
				new DataSetupItem("PrintDesign", URL_APPAREL, "PrintDesigns", [
					"ColorSpecification",
					"LibPrintDesign",
				]),
				new DataSetupItem("LibPrintDesign", URL_SIZE_CHART, "LibrariesLibPrintDesign", [
					"PrintDesign",
				]),

			]),
		]),
		new DataSetupSubsection("DataSetupSubsectionMaterialAndBOM", [
			new DataSetupGroup("DataSetupGroupProductSymbolAndCare", [
				new DataSetupItem("ProductSymbol", URL_APPAREL, "ProductSymbols"),
				new DataSetupItem("LanguageCareCategory", URL_SIZE_CHART, "LanguageAndCareCategory", [
					"Language",
				]),
				new DataSetupItem("CareSymbol", URL_SIZE_CHART, "CareSymbols", [
					"Language",
					"LibCareSymbol",
				]),
				new DataSetupItem("LibCareSymbol", URL_SIZE_CHART, "LibrariesLibCareSymbol", [
					"CareSymbol",
				]),
			]),
			new DataSetupGroup("DataSetupGroupMaterialProperties", [
				new DataSetupItem("CompositionPlacement", URL_SIZE_CHART, "CompositionPlacements", [
					"Language",
					"Composition",
				]),
				new DataSetupItem("Composition", URL_SIZE_CHART, "CompositionMaterials", [
					"Language",
					"CompositionPlacement",
				]),
				new DataSetupItem("MaterialDrape", URL_APPAREL, "Drapes"),
			]),
			new DataSetupGroup("DataSetupGroupBOMSetup", [
				new DataSetupItem("Placement", URL_SIZE_CHART, "Placement", [
					"ApparelBOM",
				]),
				new DataSetupItem("ProductStructure", URL_APPAREL, "ProductStructure", [
					"ApparelBOM",
				]),
				new DataSetupItem("BOMSectionDefinition", URL_APPAREL, "BOMSectionTemplates", [
					"ApparelBOM",
				]),
				new DataSetupItem("ApparelBOM", URL_SIZE_CHART, "StyleBOMTemplates", [
					"BOMSectionDefinition",
					"Placement",
					"ProductStructure",
					"ProductSalesRegionSpec",
				]),
			]),
		]),

		new DataSetupSubsection("DataSetupSubsectionSizeAndSizeChart", [
			createDataSetupGroupSize(),
			new DataSetupGroup("DataSetupGroupSizeChartSetup", [
				new DataSetupItem("ApparelDimension", URL_SIZE_CHART, "Dimensions", [
					"Increment",
					"ProductGroup",
				]),
				new DataSetupItem("Increment", URL_SIZE_CHART, "Increments", [
					"SizeRange",
					"ApparelDimension",
				]),
				new DataSetupItem("ProductGroup", URL_SIZE_CHART, "ProductGroups", [
					"ApparelDimension",
					"Increment",
				]),
				new DataSetupItem("SizeChart", URL_SIZE_CHART, "SizeChartTemplates", [
					"ApparelDimension",
					"Increment",
					"ProductGroup",
					"SizeRange",
				]),
			]),
			new DataSetupGroup("DataSetupGroupSizeLabel", [
				new DataSetupItem("SizeLabel", URL_SIZE_CHART, "SizeLabels", [
					"LibSizeLabel",
				]),
				new DataSetupItem("LibSizeLabel", URL_SIZE_CHART, "LibrariesLibSizeLabel", [
					"SizeLabel",
				]),
			]),
		]),
		new DataSetupSubsection("DataSetupSubsectionSpec", [
			generateSubtypeGroup("DataSetupGroupSpecItems", "SpecificationDataSheet", "SpecificationItemDefinition", URL_DATA, "SpecificationItemDefinitions", "MissingSpecItems"),
			generateSubtypeGroup("DataSetupGroupSpecSectionDefinitions", "SpecificationDataSheet", "SpecificationSectionDefinition", URL_DATA, "SpecificationSectionDefinitions", "MissingSpecSectionDefinitions"),
			new DataSetupGroup("DataSetupGroupSpecSetup", [
				new DataSetupItem("SpecificationDataSheet", URL_DATA, "SpecificationDataSheetTemplates", [
					"SpecificationSectionDefinition",
				]),
			]),
		]),
		// Food Section depends upon FormulatedProductModule
		getFoodsection(),
		new DataSetupSubsection("DataSetupSubsectionDataPackage", [
			new DataSetupGroup("", [
				new DataSetupItem("DataPackageTemplate", URL_SIZE_CHART, "DataPackageTemplates"),
				new DataSetupItem("CanvasTemplate", URL_DATA, "CanvasTemplates"),
			]),
		]),

		getDataSheetItemsImportConfigurationSection(),
	];

	if (sessionConstants.allow3d) {
		const threeDDataSetupGroup =  new DataSetupGroup("", [
			new DataSetupItem("ThreeDEnvironment", URL_APPAREL, "ThreeDEnvironments"),
			new DataSetupItem("CameraPosition", URL_APPAREL, "CameraPositions"),
		]);

		children.unshift(new DataSetupSubsection("DataSetupSubsection3DSetup", [
			threeDDataSetupGroup,
		]));

		if (hasModule(MODULE_APPAREL)) {
			threeDDataSetupGroup.items.push(new DataSetupItem("StyleSnapshotTemplate", URL_APPAREL, "SnapshotTemplates"));
		}
	}

	return new DataSetupSection(DataSetupSectionId.SPECIFICATION, children);
}

function getDataSheetItemsImportConfigurationSection(): DataSetupSubsection {
	if (hasModule(MODULE_FORMULATED_PRODUCT)) {
		return new DataSetupSubsection("DataSetupSubsectionDataSheetItemsImportConfiguration", [
			new DataSetupGroup("", [
				new DataSetupItem("RecipeItemsImportConfiguration", URL_DATA, "RecipeItemsImportConfigurations", ["RecipeItemsImportConfiguration"]),
				new DataSetupItem("FoodLabelNutrientItemsImportConfiguration", URL_DATA, "FoodLabelNutrientItemsImportConfigurations", ["FoodLabelNutrientItemsImportConfiguration"]),
			]),
		]);
	}
}

function getFoodsection(): DataSetupSubsection {
	if (hasModule(MODULE_FORMULATED_PRODUCT)) {
		return new DataSetupSubsection("DataSetupSubsectionFood", [
			new DataSetupGroup("", [
				new DataSetupItem("FoodAllergen", URL_DATA, "FoodAllergens", ["Food, AllergenSpec"]),
				new DataSetupItem("FoodClaim", URL_DATA, "FoodClaims", ["Food, ClaimSpec"]),
				new DataSetupItem("FoodIngredient", URL_DATA, "FoodIngredients", ["Food, IngredientSpec"]),
				new DataSetupItem("FoodNutrient", URL_DATA, "FoodNutrients", ["Food, NutrientSpec"]),
				new DataSetupItem("FoodLabelDataSheet", URL_DATA, "FoodLabelDataSheetTemplates"),
			]),
		]);
	}
}

function createDataSetupGroupSize(): DataSetupGroup {
	const dataSetupGroupSize = new DataSetupGroup("DataSetupGroupSize", [
		new DataSetupItem("ProductSize", URL_APPAREL, "Sizes", [
			"SizeRange",
			"SizeMap",
		]),
		new DataSetupItem("SizeRange", URL_APPAREL, "SizeRanges", [
			"ProductSize",
			"SizeMap",
			"SizeCurve",
		]),
		new DataSetupItem("SizeMap", URL_APPAREL, "SizeMaps", [
			"SizeRange",
			"ProductSize",
		]),
		new DataSetupItem("SizeSpec", URL_APPAREL, "SizeSpecs"),
	]);

	if (sessionConstants.allow3d) {
		dataSetupGroupSize.items.push(new DataSetupItem("Avatar", URL_APPAREL, "Avatars", [
			"AvatarLibrary",
			"ProductSize",
			"SizeRange",
			"Increment",
		]));

		dataSetupGroupSize.items.push(new DataSetupItem("AvatarLibrary", URL_APPAREL, "AvatarLibrary", [
			"Avatar",
		]));
	}

	return dataSetupGroupSize;
}

function getQualitySection(): DataSetupSection {
	const children = [
		new DataSetupGroup("DataSetupGroupQuality", [
			new DataSetupItem("TestSpec", URL_QUALITY, "TestSpecs"),
			new DataSetupItem("TestSpecGroup", URL_QUALITY, "TestSpecGroupsSwitchboard", [
				"TestSpec",
			]),
			new DataSetupItem("TestRun", URL_QUALITY_ADMIN, "TestRunTemplates", [
				"TestSpec",
				"TestSpecGroup",
			]),
		]),
		new DataSetupGroup("DataSetupGroupFinalInspection", [
			new DataSetupItem("Defect", URL_DATA, "InspectionDefects"),
			new DataSetupItem("InspectionErrorType", URL_DATA, "InspectionErrorTypes", [
				"FinalInspectionTemplate",
			]),
			new DataSetupItem("AcceptanceLimit", URL_DATA, "AcceptanceLimits", [
				"SamplingPlan",
				"FinalInspectionTemplate",
			]),
			new DataSetupItem("SamplingPlan", URL_DATA, "SamplingPlans", [
				"AcceptanceLimit",
				"FinalInspectionTemplate",
			]),
			new DataSetupItem("InspectionTestSpec", URL_DATA, "InspectionTestSpecs", [
				"FinalInspectionTemplate",
			]),
			new DataSetupItem("FinalInspectionTemplate", URL_DATA, "InspectionTemplates", [
				"InspectionErrorType",
				"AcceptanceLimit",
				"SamplingPlan",
				"InspectionTestSpec",
			]),
		]),
	];

	return new DataSetupSection(DataSetupSectionId.QUALITY, children);
}

function getSalesSection(): DataSetupSection {
	const children = [
		new DataSetupGroup("", [
			new DataSetupItem("SalesDivision", URL_SOURCING, "SalesDivisions"),
			new DataSetupItem("ProductSalesRegionSpec", URL_APPAREL, "ProductSalesRegionSpecs", [
				"ApparelBOM",
				"Country",
			]),
		]),
	];

	return new DataSetupSection(DataSetupSectionId.SALES, children);
}

function getSourcingSection(): DataSetupSection {
	const supplierQuoteRequestChildren = [
		new DataSetupItem("SupplierItem", URL_LIB_SUPPLIERITEM, "SupplierItemTemplates", [
			"SupplierRequestTemplate",
			"DutyRate",
		]),
	];

	if (hasModule(MODULE_FORMULATED_PRODUCT)) {
		supplierQuoteRequestChildren.push(new DataSetupItem("FoodSupplierItem", URL_LIB_SUPPLIERITEM, "FoodSupplierItemTemplates"));
	}
	supplierQuoteRequestChildren.push(new DataSetupItem("SupplierRequestTemplate", URL_LIB_SUPPLIERITEM, "SupplierRequestTemplates", [
		"SupplierRequestTemplate",
		"Routing",
		"DataPackageTemplate",
	]));

	const children = [
		new DataSetupGroup("DataSetupGroupSourcingLibraries", [
			new DataSetupItem("SourcingCapability", URL_SOURCING, "SourcingCapabilities", [
				"RoutingGroup",
			]),
			new DataSetupItem("RoutingGroup", URL_SOURCING, "RoutingGroups", [
				"SourcingCapability",
			]),
			new DataSetupItem("SubRouting", URL_APPAREL, "SubRoutings", [
				"SourcingCapability",
			]),
			new DataSetupItem("Routing", URL_LIB_SUPPLIERITEM, "RoutingTemplates", [
				"SubRouting",
			]),
			new DataSetupItem("DutyRate", URL_SOURCING_VIEW, "DutyRates", [
				"SupplierItem",
			]),
			new DataSetupItem("ShippingContainer", URL_SOURCING_VIEW, "ShippingContainers", [
				"SupplierItem",
			]),
			new DataSetupItem("SizeCurve", URL_APPAREL, "SizeCurves", [
				"SizeRange",
			]),
			new DataSetupItem("ShippingRate", URL_SOURCING_VIEW, "ShippingRates", [
				"ShippingContainer",
				"ShippingPort",
			]),
			new DataSetupItem("ShippingPort", URL_SOURCING, "ShippingPorts", [
				"Country",
				"ShippingRate",
			]),
			new DataSetupItem("ProductAlternativeSpecification", URL_APPAREL, "ProductAlternativeSpecifications"),
		]),
		new DataSetupGroup("DataSetupGroupSupplierQuoteAndRequest", supplierQuoteRequestChildren),
		new DataSetupGroup("DataSetupGroupSupplierAndFactoryReview", [
			new DataSetupItem("QuestionSubsection", URL_QUESTION_SECTION, "Subsections", [
				"Question",
				"FactoryReviewTemplate",
			]),
			new DataSetupItem("Question", URL_DATA, "Questions", [
				"QuestionSubsection",
				"FactoryReviewTemplate",
			]),
			new DataSetupItem("FactoryReviewTemplate", URL_QUALITY, "FactoryReviewTemplates", [
				"QuestionSubsection",
				"Question",
			]),
		]),
	];

	return new DataSetupSection(DataSetupSectionId.SOURCING, children);
}

function getScheduleSection(): DataSetupSection {
	const children = [
		new DataSetupGroup("", [
			new DataSetupItem("ProtoCalendar", URL_CALENDAR, "ProtoCalendarsView"),
			new DataSetupItem("Wbs", URL_WBS_ADMIN, "WbsTemplates"),
			new DataSetupItem("HolidayCalendar", URL_WBS_ADMIN, "HolidayCalendars"),
		]),
	];

	return new DataSetupSection(DataSetupSectionId.SCHEDULE, children);
}

function getProofingTemplateSection(): DataSetupSection {
	const children = [
		new DataSetupGroup("", [
			new DataSetupItem("ProofTemplate", URL_DATA, "ProofingTemplates"),
		]),
	];
	return new DataSetupSection(DataSetupSectionId.PROOFING, children);
}

function getSustainabilitySection(): DataSetupSection {
	const children = [];
	if (hasModule(MODULE_HIGG)) {
		children.push(new DataSetupGroup("DataSetupGroupHiggMSI", [
			new DataSetupItem("HiggCategory", URL_DATA, "HiggCategoryLib"),
			new DataSetupItem("HiggMaterial", URL_DATA, "HiggMaterialLib"),
		]));
	}

	if (hasModule(MODULE_APPAREL)) {
		children.push(new DataSetupGroup("DataSetupGroupSustainabilityProfile", [
			new DataSetupItem("SustainabilityProfile", URL_DATA, "SustainabilityProfileTemplates"),
		]));
		if (featureTogglesStore.MATERIAL_COMPOSITION) {
			children.push(new DataSetupGroup("DataSetupGroupSustainabilityCompositionType", [
				new DataSetupItem("SustainabilityCompositionType", URL_DATA, "SustainabilityCompositionTypes"),
			]));
		}
	}
	return new DataSetupSection(DataSetupSectionId.SUSTAINABILITY, children);
}

function getServicesSection(): DataSetupSection {
	const children = [
		new DataSetupGroup("DataSetupGroupImages", [
			new DataSetupItem("DocumentProcessingRequest", URL_QUEUES_SERVICES, "DocumentProcessingRequests"),
		]),
		new DataSetupGroup("DataSetupGroupPdf", [
			new DataSetupItem("PDFRequest", URL_QUEUES_SERVICES, "PDFRequests"),
			new DataSetupItem("PDFServiceInstance", URL_QUEUES_SERVICES, "PDFServices"),
		]),
		new DataSetupGroup("DataSetupGroupEmail", [
			new DataSetupItem("EmailHistoryItem", URL_QUEUES_SERVICES, "EmailHistory"),
		]),
		new DataSetupGroup("DataSetupGroupCalendar", [
			new DataSetupItem("CalendarRequest", URL_QUEUES_SERVICES, "CalendarRequests"),
		]),
	];

	if (featureTogglesStore.ENABLE_ASYNC_STYLE_COPY) {
		children.push(new DataSetupGroup("DataSetupGroupCopy", [
			new DataSetupItem("StyleCopyJobs", URL_QUEUES_SERVICES, "StyleCopyJobs"),
		]));
	}

	return new DataSetupSection(DataSetupSectionId.SERVICES, children, GROUP_SERVICES);
}

function getVipBoardSection(): DataSetupSection {
	const children = [
		new DataSetupGroup("", [
			new DataSetupItem("VIPModel", URL_DATA, "VIPModels"),
			new DataSetupItem("VIPMaterial", URL_DATA, "VIPMaterials"),
			new DataSetupItem("VIPGender", URL_DATA, "VIPGenders"),
			new DataSetupItem("VIPActivity", URL_DATA, "VIPActivities"),
			new DataSetupItem("VIPCollection", URL_DATA, "VIPCollections"),
			new DataSetupItem("VIPCategory", URL_DATA, "VIPCategories"),
			new DataSetupItem("VIPLine", URL_DATA, "VIPLines"),
			new DataSetupItem("VIPFamily", URL_DATA, "VIPFamilies"),
			new DataSetupItem("VIPSubFamily", URL_DATA, "VIPSubFamilies"),
			new DataSetupItem("VIPProduct", URL_DATA, "VIPProducts"),
			new DataSetupItem("VIPLook", URL_DATA, "VIPLooks"),
			new DataSetupItem("VIPPriceList", URL_DATA, "VIPPriceLists"),
			new DataSetupItem("VIPRangePlan", URL_DATA, "VIPRangePlans"),
			new DataSetupItem("VIPBuyTarget", URL_DATA, "VIPBuyTargets"),
			new DataSetupItem("VIPClientBudget", URL_DATA, "VIPClientBudgets"),
			new DataSetupItem("VIPMetadata", URL_DATA, "VIPMetadatas"),
			new DataSetupItem("VIPWarehouse", URL_DATA, "VIPWarehouses"),
			new DataSetupItem("VIPClassifierType", URL_DATA, "VIPClassifierTypes"),
			new DataSetupItem("VIPClassifier", URL_DATA, "VIPClassifiers"),
			new DataSetupItem("VIPClusterStatus", URL_DATA, "VIPClusterStatuses"),
			new DataSetupItem("VIPCluster", URL_DATA, "VIPClusters"),
			new DataSetupItem("VIPStoreCategory", URL_DATA, "VIPStoreCategories"),
			new DataSetupItem("VIPZone", URL_DATA, "VIPZones"),
			new DataSetupItem("VIPRegion", URL_DATA, "VIPRegions"),
			new DataSetupItem("VIPRatingType", URL_DATA, "VIPRatingTypes"),
			new DataSetupItem("VIPProductCategoryType", URL_DATA, "VIPProductCategoryTypes"),
			new DataSetupItem("VIPProductCategory", URL_DATA, "VIPProductCategories"),
			new DataSetupItem("VIPBoardConfiguration", URL_DATA, "VIPBoardConfigurations"),
		]),
	];

	return new DataSetupSection(DataSetupSectionId.VIPBOARDS, children);
}

function getProductComplianceSection(): DataSetupSection {
	const children = [
		new DataSetupGroup("", [
			new DataSetupItem("ProductComplianceAnalysisParameterValue", URL_DATA, "ProductComplianceAnalysisParameterValues"),
		]),
	];

	return new DataSetupSection(DataSetupSectionId.PRODUCTCOMPLIANCE, children);
}